import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Card from "@mui/material/Card";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Draggable from "react-draggable";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import EditIcon from "@mui/icons-material/Edit";

function PaperComponent(props) {
  return (
    <Draggable handle=".draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function ClientesAcordosTipo() {
  const { showAlert } = useAlert();
  const { temPermissao, userData } = useAuth();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const [clientesCombo, setClientesCombo] = useState([]);
  const [fabricantesCombo, setFabricantesCombo] = useState([]);

  const [clienteInput, setClienteInput] = useState("");
  const [fabricanteInput, setFabricanteInput] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(false);

  const [form, setForm] = useState({
    id: null,
    refcliente: "",
    reffabricante: "",
    nome: "",
    obs: "",
    inativo: 0,
  });

  const carregar = async () => {
    setLoading(true);

    const [payload, err] = await handleApi(api.post("Entidades/SelClientesAcordosTipo", {}));

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setData(Array.isArray(payload) ? payload : []);
  };

  const carregarClientesCombo = async (term = "") => {
    const [payload, err] = await handleApi(
      api.post("Campanhas/SelClientesCampanhasCombo", { term })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setClientesCombo(Array.isArray(payload) ? payload : []);
  };

  const carregarFabricantesCombo = async (term = "") => {
    const [payload, err] = await handleApi(
      api.post("Campanhas/SelFabricantesCampanhasCombo", { term })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantesCombo(Array.isArray(payload) ? payload : []);
  };

  useEffect(() => {
    carregar();
    carregarClientesCombo();
    carregarFabricantesCombo();
  }, []);

  const limparForm = () => {
    setForm({
      id: null,
      refcliente: "",
      reffabricante: "",
      nome: "",
      obs: "",
      inativo: 0,
    });
    setClienteInput("");
    setFabricanteInput("");
  };

  const abrirNovo = () => {
    setEditando(false);
    limparForm();
    setOpenDialog(true);
  };

  const abrirEditar = (row) => {
    setEditando(true);

    setForm({
      id: row.id,
      refcliente: row.refcliente || "",
      reffabricante: row.reffabricante || "",
      nome: row.nome || "",
      obs: row.obs || "",
      inativo: Number(row.inativo || 0),
    });

    setClienteInput(row.cliente ? `${row.cliente} (${row.refcliente})` : row.refcliente || "");
    setFabricanteInput(
      row.fabricante ? `${row.fabricante} (${row.reffabricante})` : row.reffabricante || ""
    );

    setOpenDialog(true);
  };

  const guardar = async () => {
    if (!form.refcliente || !form.reffabricante || !form.nome) {
      showAlert("Preenche cliente, fabricante e nome do acordo.", "warning");
      return;
    }

    setLoading(true);

    const body = {
      ...form,
      criado_por: userData?.id || null,
      atualizado_por: userData?.id || null,
    };

    const endpoint = editando ? "Entidades/UpdClienteAcordoTipo" : "Entidades/InsClienteAcordoTipo";

    const [_, err, meta] = await handleApi(api.post(endpoint, body));

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Acordo gravado com sucesso.", meta?.severity || "success");

    setOpenDialog(false);
    await carregar();
  };

  const rowActions = [
    {
      id: "editar",
      icon: <EditIcon fontSize="small" />,
      tooltip: "Editar acordo",
      color: "primary",
      disabled: () => !temPermissao("Menu Comercial", "u"),
      onClick: abrirEditar,
    },
  ];

  const columns = useMemo(
    () => [
      { Header: "Cliente", accessor: "cliente" },
      { Header: "Ref. Cliente", accessor: "refcliente" },
      { Header: "NIF", accessor: "nif" },
      { Header: "Fabricante", accessor: "fabricante" },
      { Header: "Ref. Fabricante", accessor: "reffabricante" },
      { Header: "Acordo", accessor: "nome" },
      { Header: "Observações", accessor: "obs" },
      {
        Header: "Ativo",
        accessor: "inativo",
        align: "center",
        width: 90,
        filterType: "simNaoInverso",
        Cell: ({ value }) => (Number(value || 0) === 1 ? "Não" : "Sim"),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6">Acordos cliente / fabricante</MDTypography>

              <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Comercial", "w")}
                  onClick={abrirNovo}
                >
                  Novo Acordo
                </MDButton>
              </MDBox>
            </MDBox>

            <ProjectTable
              tableKey="clientes-acordos-tipo"
              version="v1"
              columns={columns}
              data={data}
              rowActions={rowActions}
              loading={loading}
              emptyMessage="Sem acordos cliente / fabricante para apresentar."
              minWidth={1250}
              maxHeight="68vh"
              showKeepFilters
            />
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
          {editando ? "Editar acordo" : "Novo acordo"}
        </DialogTitle>

        <DialogContent>
          <MDBox display="grid" gap={1.5} mt={1}>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Cliente</div>
              <Autocomplete
                size="small"
                options={clientesCombo}
                filterOptions={(x) => x}
                inputValue={clienteInput}
                getOptionLabel={(option) => (option?.nome ? `${option.nome} (${option.ref})` : "")}
                value={clientesCombo.find((c) => c.ref === form.refcliente) || null}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setClienteInput(value);
                    setForm((p) => ({ ...p, refcliente: "" }));
                    carregarClientesCombo(value);
                  }

                  if (reason === "clear") {
                    setClienteInput("");
                  }
                }}
                onChange={(_, value) => {
                  setForm((p) => ({ ...p, refcliente: value?.ref || "" }));
                  setClienteInput(value?.nome ? `${value.nome} (${value.ref})` : "");
                }}
                renderInput={(params) => <TextField {...params} placeholder="Selecione..." />}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Fabricante</div>
              <Autocomplete
                size="small"
                options={fabricantesCombo}
                filterOptions={(x) => x}
                inputValue={fabricanteInput}
                getOptionLabel={(option) => (option?.nome ? `${option.nome} (${option.ref})` : "")}
                value={fabricantesCombo.find((f) => f.ref === form.reffabricante) || null}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setFabricanteInput(value);
                    setForm((p) => ({ ...p, reffabricante: "" }));
                    carregarFabricantesCombo(value);
                  }

                  if (reason === "clear") {
                    setFabricanteInput("");
                  }
                }}
                onChange={(_, value) => {
                  setForm((p) => ({ ...p, reffabricante: value?.ref || "" }));
                  setFabricanteInput(value?.nome ? `${value.nome} (${value.ref})` : "");
                }}
                renderInput={(params) => <TextField {...params} placeholder="Selecione..." />}
              />
            </div>

            <MDInput
              label="Nome do acordo"
              value={form.nome}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  nome: (e.target.value || "").toUpperCase(),
                }))
              }
              fullWidth
            />

            <MDInput
              label="Observações"
              value={form.obs}
              onChange={(e) => setForm((p) => ({ ...p, obs: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Number(form.inativo || 0) === 1}
                  onChange={(e) => setForm((p) => ({ ...p, inativo: e.target.checked ? 1 : 0 }))}
                  size="small"
                />
              }
              label="Inativo"
            />
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ flexWrap: "wrap" }}>
          <MDButton color="secondary" onClick={() => setOpenDialog(false)}>
            Cancelar
          </MDButton>

          <MDButton color="success" onClick={guardar}>
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
